import { useCallback, useEffect, useRef } from 'react'

/** Custom hook for managing timeouts with automatic cleanup */
export function useTimeout() {
	const timeoutRefs = useRef<Set<NodeJS.Timeout>>(new Set())

	/** Set a timeout with automatic cleanup */
	const setTimeout = useCallback((callback: () => void, delay: number): NodeJS.Timeout => {
		const timeoutId = globalThis.setTimeout(() => {
			timeoutRefs.current.delete(timeoutId)
			callback()
		}, delay)

		timeoutRefs.current.add(timeoutId)
		return timeoutId
	}, [])

	/** Clear a specific timeout */
	const clearTimeout = useCallback((timeoutId: NodeJS.Timeout) => {
		globalThis.clearTimeout(timeoutId)
		timeoutRefs.current.delete(timeoutId)
	}, [])

	/** Clear all timeouts */
	const clearAllTimeouts = useCallback(() => {
		timeoutRefs.current.forEach((timeoutId) => {
			globalThis.clearTimeout(timeoutId)
		})
		timeoutRefs.current.clear()
	}, [])

	/** Set an interval with automatic cleanup */
	const setInterval = useCallback((callback: () => void, delay: number): NodeJS.Timeout => {
		const intervalId = globalThis.setInterval(callback, delay)
		timeoutRefs.current.add(intervalId)
		return intervalId
	}, [])

	/** Clear a specific interval */
	const clearInterval = useCallback((intervalId: NodeJS.Timeout) => {
		globalThis.clearInterval(intervalId)
		timeoutRefs.current.delete(intervalId)
	}, [])

	// Cleanup all timeouts and intervals on unmount
	useEffect(() => {
		return () => {
			clearAllTimeouts()
		}
	}, [clearAllTimeouts])

	return {
		setTimeout,
		clearTimeout,
		clearAllTimeouts,
		setInterval,
		clearInterval,
		activeTimeouts: timeoutRefs.current.size,
	}
}

/** Hook for a single timeout that auto-cleans up */
export function useSingleTimeout(callback: () => void, delay: number | null) {
	const timeoutRef = useRef<NodeJS.Timeout | null>(null)
	const callbackRef = useRef(callback)

	// Update callback ref when callback changes
	useEffect(() => {
		callbackRef.current = callback
	})

	useEffect(() => {
		// Clear existing timeout
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current)
			timeoutRef.current = null
		}

		// Set new timeout if delay is provided
		if (delay !== null) {
			timeoutRef.current = setTimeout(() => {
				callbackRef.current()
				timeoutRef.current = null
			}, delay)
		}

		// Cleanup function
		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current)
				timeoutRef.current = null
			}
		}
	}, [delay])

	const clearCurrentTimeout = useCallback(() => {
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current)
			timeoutRef.current = null
		}
	}, [])

	return { clearTimeout: clearCurrentTimeout }
}
